import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, X, ChevronRight, Receipt } from "lucide-react";
import { api } from "@/lib/api";
import { INVOICE_STATUS_META, inr } from "./lib";

// "Unpaid" filter removed: an issued invoice with zero payment IS unpaid —
// same signal, two labels. Operators pick "Issued" to see the same set.
const VIEWS = [
  { key: "",                 label: "All" },
  { key: "draft",            label: "Drafts" },
  { key: "issued",           label: "Issued" },
  { key: "partially_paid",   label: "Partially Paid",  pay: "partially_paid" },
  { key: "paid",             label: "Paid",            pay: "paid" },
  { key: "gate_pass_issued", label: "Gate Pass" },
  { key: "cancelled",        label: "Cancelled" },
];

export default function InvoicesList() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [view, setView] = useState("");
  const [overview, setOverview] = useState(null);

  const load = () => {
    setLoading(true);
    const params = {};
    if (q.trim()) params.q = q.trim();
    const v = VIEWS.find((x) => x.key === view);
    if (v?.pay) params.payment_status = v.pay;
    else if (view) params.status = view;
    api.get("/billing/invoices", { params })
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
    api.get("/billing/invoices/overview").then((r) => setOverview(r.data.kpis));
  };

  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
     
  }, [q, view]);

  return (
    <div className="space-y-4" data-testid="invoices-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight">Invoices</h1>
          <p className="text-[12.5px] text-[#667085] mt-0.5">
            {rows.length} invoice{rows.length === 1 ? "" : "s"} · GST + Non-GST billing.
          </p>
        </div>
        <Link to="/invoices/new" data-testid="invoices-new-btn"
              className="h-10 px-4 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5">
          <Plus size={13} /> New Invoice
        </Link>
      </header>

      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="invoices-kpis">
          <MiniKpi label="Ready to bill" value={overview.ready_for_billing} tone="amber" />
          <MiniKpi label="Payment pending" value={(overview.issued || 0) + (overview.partially_paid || 0)} tone="red" />
          <MiniKpi label="Paid" value={overview.paid} tone="green" />
          <MiniKpi label="GP pending" value={overview.gate_pass_pending} tone="steel" />
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex flex-wrap gap-1 bg-white rounded-lg border border-[#E4E7EC] p-1 overflow-x-auto">
          {VIEWS.map((v) => (
            <button key={v.key || "all"} onClick={() => setView(v.key)}
                    data-testid={`invoices-view-${v.key || "all"}`}
                    className={`h-8 px-3 rounded-md text-[11.5px] font-semibold whitespace-nowrap ${
                      view === v.key ? "bg-[#1d3557] text-white" : "text-[#667085] hover:text-[#1d3557]"
                    }`}>
              {v.label}
            </button>
          ))}
        </div>
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a8dadc]" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search invoice #, customer, phone, vehicle, JC #…"
                 data-testid="invoices-search"
                 className="w-full h-10 pl-9 pr-9 rounded-lg border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none" />
          {q && (
            <button onClick={() => setQ("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a8dadc] hover:text-[#e63946]">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      <section className="bg-white rounded-xl border border-[#E4E7EC]">
        {loading ? (
          <div className="p-10 text-center text-[13px] text-[#667085]">Loading invoices…</div>
        ) : rows.length === 0 ? (
          <div className="p-14 text-center" data-testid="invoices-empty">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#F1FAEE] grid place-items-center text-[#457b9d] mb-3">
              <Receipt size={20} />
            </div>
            <div className="text-[14px] font-semibold text-[#1d3557]">No invoices yet</div>
            <p className="text-[12px] text-[#667085] mt-1">Bill a completed job card or start a fresh invoice.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="invoices-table">
              <thead>
                <tr className="text-left text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] border-b border-[#F1FAEE]">
                  <th className="px-5 py-2.5">Number</th>
                  <th className="px-3 py-2.5">Customer</th>
                  <th className="px-3 py-2.5">Vehicle</th>
                  <th className="px-3 py-2.5">Job Card</th>
                  <th className="px-3 py-2.5">Type</th>
                  <th className="px-3 py-2.5 text-right">Total</th>
                  <th className="px-3 py-2.5 text-right">Received</th>
                  <th className="px-3 py-2.5 text-right">Balance</th>
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-5 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const st = INVOICE_STATUS_META[r.status] || INVOICE_STATUS_META.draft;
                  return (
                    <tr key={r.id} data-testid={`invoice-row-${r.id}`}
                        onClick={() => nav(`/invoices/${r.id}`)}
                        className="border-b border-[#F1FAEE] hover:bg-[#F7F9FC] cursor-pointer">
                      <td className="px-5 py-3 font-mono font-semibold text-[#1d3557]">{r.number}</td>
                      <td className="px-3 py-3 text-[#1d3557]">{r.customer_snapshot?.name || "—"}</td>
                      <td className="px-3 py-3 font-mono text-[11.5px] text-[#667085]">{r.vehicle_snapshot?.reg_number || "—"}</td>
                      <td className="px-3 py-3 font-mono text-[11.5px] text-[#667085]">{r.job_card_number || "—"}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          r.invoice_type === "gst" ? "bg-[#457b9d]/15 text-[#1d3557]" : "bg-[#a8dadc]/25 text-[#457b9d]"
                        }`}>{r.invoice_type === "gst" ? "GST" : "Non-GST"}</span>
                      </td>
                      <td className="px-3 py-3 text-right font-mono font-bold">{inr(r.totals?.invoice_total)}</td>
                      <td className="px-3 py-3 text-right font-mono text-[#0F9256]">{inr(r.paid_amount)}</td>
                      <td className="px-3 py-3 text-right font-mono text-[#e63946]">{inr(r.balance)}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ${st.chip}`}>
                          {st.label}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right text-[#a8dadc]"><ChevronRight size={14} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function MiniKpi({ label, value, tone }) {
  const map = {
    red:   "border-[#e63946]/30 text-[#e63946]",
    amber: "border-[#F79009]/30 text-[#B54708]",
    green: "border-[#12B76A]/30 text-[#0F9256]",
    steel: "border-[#E4E7EC] text-[#457b9d]",
  };
  return (
    <div className={`rounded-lg bg-white border ${map[tone]} p-3`}>
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">{label}</div>
      <div className="text-[22px] font-bold font-mono tabular-nums mt-0.5">{value ?? 0}</div>
    </div>
  );
}
